import * as React from "react";
import { Field, useFormikContext, getIn, FieldArray } from "formik";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import * as Immutable from "immutable";
import random from "lodash/random";
import { defaultCompare } from "logic/DefaultCompare";
import { Col, Input } from "components/bootstrap";
import Select from "components/common/Select";
import { InputOptionalInfo as Opt, FormikInput, IconButton } from "components/common";
import { Properties } from "views/logic/fieldtypes/FieldType";
import useAggregationFunctions from "views/hooks/useAggregationFunctions";
import { percentileOptions, percentageStrategyOptions, thresholdsSupportedVisualizations } from "views/Constants";
import isFunctionAllowsUnit from "views/logic/isFunctionAllowsUnit";
import FieldUnit from "views/components/aggregationwizard/units/FieldUnit";
import useFeature from "hooks/useFeature";
import { UNIT_FEATURE_FLAG } from "views/components/visualizations/Constants";
import ThresholdFormItem from "views/components/aggregationwizard/metric/ThresholdFormItem";
import { colors } from "views/components/visualizations/Colors";
import FieldSelect from "../FieldSelect";
const Wrapper = styled.div``;
export const ActionWrapper = styled.div`
  width: 25px;
  height: 25px;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const ThresholdsContainer = styled.div(
  ({ theme }) => `
  padding-bottom: ${theme.spacings.sm};
`
);
const sortByLabel = ({ label: label1 }, { label: label2 }) => defaultCompare(label1, label2);
const hasProperty = (fieldType, properties) => {
  var _a, _b;
  const fieldProperties = (_b = (_a = fieldType == null ? void 0 : fieldType.type) == null ? void 0 : _a.properties) != null ? _b : Immutable.Set();
  return properties.map((property) => fieldProperties.contains(property)).find((result) => result === false) === void 0;
};
const Metric = ({ index }) => {
  const unitFeatureEnabled = useFeature(UNIT_FEATURE_FLAG);
  const metricFieldSelectRef = useRef(null);
  const { data: functions, isLoading } = useAggregationFunctions();
  const functionOptions = useMemo(
    () => isLoading ? [] : Object.values(functions).map(({ type, description }) => ({ label: description, value: type })).sort(sortByLabel),
    [functions, isLoading]
  );
  const {
    values: { metrics, visualization },
    errors: { metrics: metricsError },
    setFieldValue
  } = useFormikContext();
  const currentMetric = metrics[index];
  const currentFunction = currentMetric.function;
  const hasFieldOption = currentFunction !== "percentage";
  const isFieldRequired = !["count", "percentage"].includes(currentFunction);
  const isPercentile = currentFunction === "percentile";
  const isPercentage = currentFunction === "percentage";
  const requiresNumericField = isPercentage && currentMetric.strategy === "SUM" || !["card", "count", "latest", "percentage"].includes(currentFunction);
  const isFieldQualified = useCallback(
    (field) => {
      if (!requiresNumericField) {
        return true;
      }
      return hasProperty(field, [Properties.Numeric]);
    },
    [requiresNumericField]
  );
  const [functionIsSettled, setFunctionIsSettled] = useState(false);
  const onFunctionChange = useCallback(
    (newValue) => {
      setFieldValue(`metrics.${index}.function`, newValue);
      setFunctionIsSettled(true);
    },
    [setFieldValue, index]
  );
  useEffect(() => {
    const metricError = getIn(metricsError == null ? void 0 : metricsError[index], "field");
    if (metricError && functionIsSettled) {
      metricFieldSelectRef.current.focus();
    }
  }, [functionIsSettled, metricsError, index, metricFieldSelectRef]);
  const showUnitType = unitFeatureEnabled && isFunctionAllowsUnit(currentFunction);
  const createThreshold = () => {
    const palette = colors[random(0, colors.length - 1)];
    const randomColor = palette[random(0, palette.length - 1)];
    return {
      color: randomColor,
      name: "",
      value: 0
    };
  };
  const addThresholds = () => {
    setFieldValue(`metrics.${index}.thresholds`, [...currentMetric.thresholds, createThreshold()]);
  };
  const showThresholdSettings = thresholdsSupportedVisualizations.includes(visualization == null ? void 0 : visualization.type);
  const onRemoveThreshold = useCallback(
    (thresholdIndex) => {
      setFieldValue(
        `metrics.${index}.thresholds`,
        metrics[index].thresholds.filter((_, i) => i !== thresholdIndex)
      );
    },
    [index, metrics, setFieldValue]
  );
  return /* @__PURE__ */ React.createElement(Wrapper, { "data-testid": `metric-${index}` }, /* @__PURE__ */ React.createElement(Col, { sm: 11 }, /* @__PURE__ */ React.createElement(Field, { name: `metrics.${index}.function` }, ({ field: { name, value }, meta: { error } }) => /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "metric-function-select",
      label: "Function",
      error,
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(
      Select,
      {
        options: functionOptions,
        clearable: false,
        name,
        value,
        placeholder: "Select a function",
        size: "small",
        onChange: onFunctionChange
      }
    )
  ))), hasFieldOption && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Col, { sm: 11 }, /* @__PURE__ */ React.createElement(Field, { name: `metrics.${index}.field` }, ({ field: { name, value, onChange }, meta: { error } }) => /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "metric-field",
      label: "Field",
      error,
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(
      FieldSelect,
      {
        id: "metric-field-select",
        selectRef: metricFieldSelectRef,
        allowCreate: true,
        onChange: (fieldName) => {
          onChange({ target: { name, value: fieldName } });
        },
        clearable: !isFieldRequired,
        isFieldQualified,
        name,
        value,
        ariaLabel: "Select a field"
      }
    )
  ))), showUnitType && /* @__PURE__ */ React.createElement("div", { className: "col-sm-1" }, /* @__PURE__ */ React.createElement(FieldUnit, { field: metrics == null ? void 0 : metrics[index].field }))), isPercentile && /* @__PURE__ */ React.createElement(Col, { sm: 11 }, /* @__PURE__ */ React.createElement(Field, { name: `metrics.${index}.percentile` }, ({ field: { name, value, onChange }, meta: { error } }) => /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "metric-percentile-select",
      label: "Percentile",
      error,
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(
      Select,
      {
        options: percentileOptions,
        clearable: false,
        name,
        value,
        "aria-label": "Select percentile",
        size: "small",
        onChange: (newValue) => onChange({ target: { name, value: newValue } })
      }
    )
  ))), isPercentage && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Col, { sm: 11 }, /* @__PURE__ */ React.createElement(Field, { name: `metrics.${index}.strategy` }, ({ field: { name, value, onChange }, meta: { error } }) => /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "metric-percentage-strategy-select",
      label: "Strategy",
      error,
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(
      Select,
      {
        options: percentageStrategyOptions,
        clearable: false,
        name,
        value: value != null ? value : "COUNT",
        "aria-label": "Select strategy",
        size: "small",
        onChange: (newValue) => onChange({ target: { name, value: newValue } })
      }
    )
  ))), /* @__PURE__ */ React.createElement(Col, { sm: 11 }, /* @__PURE__ */ React.createElement(Field, { name: `metrics.${index}.field` }, ({ field: { name, value, onChange }, meta: { error } }) => /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "metric-field",
      label: "Field",
      error,
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(
      FieldSelect,
      {
        id: "metric-field-select",
        selectRef: metricFieldSelectRef,
        onChange: (fieldName) => onChange({ target: { name, value: fieldName } }),
        clearable: !isFieldRequired,
        isFieldQualified,
        name,
        value,
        ariaLabel: "Select a field"
      }
    )
  )))), /* @__PURE__ */ React.createElement(Col, { sm: 11 }, /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      id: "name",
      label: /* @__PURE__ */ React.createElement(React.Fragment, null, "Name ", /* @__PURE__ */ React.createElement(Opt, null)),
      bsSize: "small",
      placeholder: "Specify display name",
      name: `metrics.${index}.name`,
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    }
  )), showThresholdSettings && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Col, { sm: 11 }, /* @__PURE__ */ React.createElement(Field, { name: `metrics.${index}.showThresholds` }, ({ field: { name, value = false } }) => /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      wrapperClassName: "col-sm-12",
      label: "Show line thresholds",
      id: `${name}-input`,
      name,
      onChange: () => {
        var _a;
        const newVal = !value;
        setFieldValue(name, newVal);
        if (newVal && !((_a = currentMetric.thresholds) == null ? void 0 : _a.length)) {
          setFieldValue(`metrics.${index}.thresholds`, [createThreshold()]);
        }
      }
    }
  ))), currentMetric.showThresholds && /* @__PURE__ */ React.createElement(Col, { sm: 1 }, /* @__PURE__ */ React.createElement(IconButton, { onClick: addThresholds, size: "sm", name: "add", title: "Add a threshold" })), currentMetric.showThresholds && /* @__PURE__ */ React.createElement(
    FieldArray,
    {
      name: `metrics.${index}.thresholds`,
      validateOnChange: false,
      render: () => {
        var _a, _b;
        return /* @__PURE__ */ React.createElement(ThresholdsContainer, null, (_b = (_a = metrics == null ? void 0 : metrics[index]) == null ? void 0 : _a.thresholds) == null ? void 0 : _b.map((_, tIndex) => /* @__PURE__ */ React.createElement(
          ThresholdFormItem,
          {
            key: `${index}-${tIndex}`,
            thresholdIndex: tIndex,
            metricIndex: index,
            onRemove: () => onRemoveThreshold(tIndex)
          }
        )));
      }
    }
  )));
};
export default Metric;
